import { Upload } from 'lucide-react';
import {
  useCallback,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type ReactNode,
} from 'react';

import { cn } from '@/lib/utils';

export interface FileDropzoneProps {
  /** Recebe os arquivos aceitos (já filtrados por `maxSize`, se definido). */
  onFilesSelected: (files: File[]) => void;
  /**
   * Disparado para todo conjunto de arquivos recebido (drag-drop ou input),
   * **antes** da validação de tamanho. Útil para telemetria/log do consumidor.
   */
  onDrop?: (files: File[]) => void;
  /** Disparado quando há arquivos rejeitados por excederem `maxSize`. */
  onSizeRejected?: (rejected: File[]) => void;
  /** MIME types ou extensões aceitas (passa direto pro input/file). */
  accept?: string;
  /** Multi-arquivo. Default: false. */
  multiple?: boolean;
  /** Tamanho máximo aceito por arquivo, em bytes. Acima disso → `onSizeRejected`. */
  maxSize?: number;
  disabled?: boolean;
  /** Conteúdo interno. Default: ícone + título + hint. */
  children?: ReactNode;
  title?: string;
  hint?: string;
  className?: string;
  /** Marcador para testes/migrações. */
  'data-testid'?: string;
  /**
   * Marcador do INPUT interno (E56.F3.T06). Sem ele o input carrega sempre o
   * mesmo `file-dropzone-input`, e uma tela com dois dropzones perde a
   * identificação de qual é qual — foi o que deixou os specs da E8 apontando
   * para um seletor que não existia mais.
   */
  inputTestId?: string;
  /**
   * Nome acessível do input (E56.F3.T04). O input file É o controle — quem o
   * enxerga por leitor de tela precisa saber o que ele recebe. Sem isto, a
   * auditoria axe reprova com `label`, de impacto crítico.
   */
  inputLabel?: string;
}

/**
 * Primitive de área de drop com input file invisível.
 *
 * Validação opcional de tamanho via `maxSize` (bytes). Tipo de arquivo permanece
 * responsabilidade do consumidor (use `accept` para hint nativa do browser e
 * valide o MIME final em `onFilesSelected`).
 */
export function FileDropzone({
  inputTestId,
  inputLabel,
  onFilesSelected,
  onDrop,
  onSizeRejected,
  accept,
  multiple = false,
  maxSize,
  disabled = false,
  children,
  title = 'Arraste arquivos aqui',
  hint = 'Ou clique para selecionar',
  className,
  ...rest
}: FileDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();
  const [dragging, setDragging] = useState(false);

  const dispatchFiles = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      onDrop?.(files);
      if (maxSize != null) {
        const accepted: File[] = [];
        const rejected: File[] = [];
        for (const f of files) (f.size > maxSize ? rejected : accepted).push(f);
        if (rejected.length > 0) onSizeRejected?.(rejected);
        if (accepted.length > 0) onFilesSelected(accepted);
      } else {
        onFilesSelected(files);
      }
    },
    [maxSize, onDrop, onFilesSelected, onSizeRejected],
  );

  const handleDrop = useCallback(
    (e: DragEvent<HTMLLabelElement>) => {
      e.preventDefault();
      setDragging(false);
      if (disabled) return;
      dispatchFiles(Array.from(e.dataTransfer.files));
    },
    [disabled, dispatchFiles],
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      dispatchFiles(Array.from(e.target.files ?? []));
      e.target.value = '';
    },
    [dispatchFiles],
  );

  return (
    /*
     * E56.F3.T04: a área é um `<label>` do próprio input, não mais uma `div`
     * com `role="button"` e `tabIndex`. Um controle interativo dentro de outro é
     * a violação `nested-interactive` (séria) da axe — que só apareceu quando o
     * wizard passou a abrir no envio de documentos e a auditoria da E13.F10
     * finalmente alcançou esta tela.
     *
     * O label resolve os três lados de uma vez: o clique em qualquer ponto abre
     * o seletor por comportamento NATIVO (sem handler), o input segue focável
     * na ordem de tabulação e o conteúdo do label vira o nome acessível dele.
     */
    <label
      htmlFor={inputId}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      className={cn(
        'flex cursor-pointer flex-col items-center justify-center gap-2 border-2 border-dashed border-border p-8 text-center transition-colors duration-fast ease-sc hover:border-accent',
        dragging && 'border-accent bg-accent-bg',
        disabled && 'cursor-not-allowed opacity-50 hover:border-border',
        className,
      )}
      data-testid={rest['data-testid'] ?? 'file-dropzone'}
    >
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        onChange={handleChange}
        className="sr-only"
        aria-label={inputLabel ?? title}
        data-testid={inputTestId ?? 'file-dropzone-input'}
      />
      {children ?? (
        <>
          <Upload className="h-8 w-8 text-fg-faint" aria-hidden />
          <span className="text-body-md font-medium text-fg">{title}</span>
          <span className="text-body-sm text-fg-muted">{hint}</span>
        </>
      )}
    </label>
  );
}
